import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MergeRuleSelector from './MergeRuleSelector'
import SubTitle from '../../components/template/SubTitle';
import { classNames } from '../../components/utils/Utils';
import { Link, Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBoxesPacking, faChartLine, faFlask, faGear, faListOl, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import MainTitle from '../../components/template/MainTitle';
import ButtonWrap from '../../components/template/ButtonWrap';
import api from '../../components/utils/api';
import Spinner from '../../components/template/Spinner';
import { recordRecentModel } from '../../components/utils/recentModels';

const PostActionPage = () => {
    const { generalModelId, mergeRuleId } = useParams()

    const [generalModel, setGeneralModel] = useState([])
    const [selectedMergeRule, setSelectedMergeRule] = useState({})
    const [isReportDisabled, setIsReportDisabled] = useState(true)
    // the two loading legs behind the workspace overlay: the rule list
    // (fetched by MergeRuleSelector) and the selected rule itself
    const [isMergeRulesLoading, setIsMergeRulesLoading] = useState(true)
    const [isRuleLoading, setIsRuleLoading] = useState(false)

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        const fetchGeneralModel = async () => {
            try {
                const res = await api.get(`/api/general-models/${generalModelId}`)
                setGeneralModel(res.data)
                setIsReportDisabled(res.data.name.includes("Ensemble"))
            } catch (err) {
                console.error(err)
                // the selector never fetches without a model id, so its onLoaded
                // would never fire and the overlay would stay up
                setIsMergeRulesLoading(false)
                // TODO: update error handling
                if (err.response.status === 404) {
                    alert(err.response.data.message + " Return to the model version selection page.")
                    navigate('/')
                }
            }
        }
        if (generalModelId) fetchGeneralModel()
    }, [generalModelId])

    useEffect(() => {
        const fetchMergeRule = async () => {
            setIsRuleLoading(true)
            try {
                const res = await api.get(`/api/merge-rules/${mergeRuleId}`)
                setSelectedMergeRule(res.data)
            } catch (err) {
                console.error(err)
            } finally {
                setIsRuleLoading(false)
            }
        }
        if (mergeRuleId) fetchMergeRule()
    }, [mergeRuleId])

    const handleMergeRulesLoaded = useCallback(() => setIsMergeRulesLoading(false), [])

    useEffect(() => {
        if (generalModel.id && selectedMergeRule.id) recordRecentModel({
            modelId: generalModel.id,
            modelName: generalModel.name,
            mergeRuleId: selectedMergeRule.id,
            mergeRuleName: selectedMergeRule.name,
        })
    }, [generalModel, selectedMergeRule])

    return (
        <div className='default-background'>
            <div className='mb-5 md:flex items-end justify-between'>
                <div className='min-w-0'>
                    <SubTitle title="Post Action" />
                    <div className='md:flex flex-wrap items-center gap-x-2 gap-y-2 min-w-0'>
                        <MainTitle title={`${generalModel.name}`} />
                        <div className='mt-2 md:mt-0 shrink-0 whitespace-nowrap'>
                            {!isReportDisabled && <Link
                                to={`/training-report/${generalModel.id}`}
                                target="_blank" rel="noopener noreferrer"
                                className='bg-violet-100 text-sm border border-violet-500 text-violet-500 hover:bg-violet-200 px-2 py-1 rounded-xl'
                            >Training Report</Link>}
                            {generalModel.gcs_path && <a
                                href={`https://console.cloud.google.com/storage/browser/${generalModel.gcs_path}`}
                                target="_blank" rel="noopener noreferrer"
                                className='ml-2 bg-violet-100 text-sm border border-violet-500 text-violet-500 hover:bg-violet-200 px-2 py-1 rounded-xl'
                            >Google Storage</a>}
                        </div>
                    </div>
                </div>
                <div className='text-left'>
                    <ButtonWrap contents={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate('/model-selection')} />
                </div>
            </div>

            <div className='relative'>
                <SubTitle title="Merge Rules" />
                <div className={classNames('py-2 grid gap-3 ', selectedMergeRule.hasOwnProperty('id') ? "md:grid-cols-5 md:h-[80vh]" : "")}>
                    <MergeRuleSelector
                        selectedMergeRule={selectedMergeRule}
                        generalModel={generalModel}
                        onLoaded={handleMergeRulesLoaded}
                    />
                    {selectedMergeRule.hasOwnProperty('id') && <div className='md:col-span-4'>
                        <div className='rounded border border-gray-200 h-full min-h-[300px] max-h-[80vh] p-5 overflow-auto'>
                            <ul className='grid grid-cols-6 mb-6 text-sm md:text-base'>
                                {[
                                    { title: 'detail', icon: faNewspaper, label: 'Detail' },
                                    { title: 'convert', icon: faGear, label: 'Convert' },
                                    { title: 'thresholds', icon: faListOl, label: 'Thresholds' },
                                    { title: 'evaluations', icon: faChartLine, label: 'Evaluations' },
                                    { title: 'live-test', icon: faFlask, label: 'Live Test' },
                                    { title: 'services', icon: faBoxesPacking, label: 'Services' },
                                ].map((e, i) => <li key={i} className={classNames(e.title === location.pathname.split("/").pop() ? "text-violet-500" : "text-gray-500")}>
                                    <Link to={`/post-action/models/${generalModel.id}/merge-rules/${selectedMergeRule.id}/${e.title}`}><FontAwesomeIcon icon={e.icon} /><br />{e.label || e.title[0].toUpperCase() + e.title.slice(1)}</Link>
                                </li>)}
                            </ul>
                            <Outlet />
                        </div>
                    </div>}
                </div>
                {(isMergeRulesLoading || isRuleLoading) && !selectedMergeRule.hasOwnProperty('id') && <div className='absolute inset-0 bg-white/70 flex items-center justify-center'>
                    <Spinner />
                </div>}
            </div>
        </div>
    )
}

export default PostActionPage